import { useBooleanState } from '@calendly/hooks';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { AlertWarningTriangleIcon } from '@calendly/ui/components/icons';
import { useRef, useState, memo } from 'react';
import { MESSAGE_STATUS } from '../constants';
import { FeedbackPopup } from '../feedbackPopup';
import { IconButtons } from '../iconButtons';
import { Markdown } from '../markdown';
import { useVisibilityTracking } from './useVisibilityTracking';
const aiMessageContainerCss = "a1qjmbns";
const errorContainerCss = "enbj78o";
const messageActionsCss = "mgyj2pp";
const visibilityTrackerCss = "voar1ph";

/**
 * AiResponseMessage component that renders AI-generated responses with different states.
 *
 *
 * @param {Object} props - Component props
 * @param {string} props.content - The AI response content to display
 * @param {string} props.status - The status of the message from MESSAGE_STATUS constants
 * @param {string} props.queryId - The id of the query
 * @param {string} props.responseId - The id of the response
 * @param {Object} props.analyticsHandlers - Analytics event handlers
 * @param {Function} props.focusComposer - Function to focus the composer/text box
 *
 * @returns {JSX.Element} Rendered component based on the message status
 *
 * @example
 * ```jsx
 * <AiResponseMessage
 *   content="Here's your AI-generated response..."
 *   status={MESSAGE_STATUS.SUCCESS}
 *   analyticsHandlers={analyticsHandlers}
 * />
 * ```
 */
export const AiResponseMessage = memo(({
  content,
  status,
  queryId,
  responseId,
  analyticsHandlers = {},
  focusComposer
}) => {
  const responseMessageRef = useRef(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [isFeedbackOpen, openFeedback, closeFeedback] = useBooleanState(false);

  // Use custom hook for visibility tracking (only for success messages)
  const {
    topElementRef,
    bottomElementRef
  } = useVisibilityTracking({
    status,
    queryId,
    responseId,
    trackResponseView: analyticsHandlers.trackResponseView
  });
  const trackThumbsFeedback = isThumbsUp => {
    if (analyticsHandlers.trackThumbsFeedback) {
      analyticsHandlers.trackThumbsFeedback(isThumbsUp, responseId);
    }
  };
  const handleLike = () => {
    if (isLiked) {
      setIsLiked(false);
    } else {
      setIsLiked(true);
      setIsDisliked(false);
      trackThumbsFeedback(true);
    }
  };
  const handleDislike = () => {
    if (isDisliked) {
      setIsDisliked(false);
    } else {
      setIsDisliked(true);
      setIsLiked(false);
      openFeedback();
      trackThumbsFeedback(false);
    }
  };

  // Focus composer to shift focus away from thumbs down button after survey is closed
  const focusComposerDelayed = () => {
    setTimeout(() => {
      if (focusComposer) {
        focusComposer();
      }
    }, 0);
  };
  const handleConfirm = (feedbackSelection, feedbackDetails) => {
    closeFeedback();
    focusComposerDelayed();
    if (analyticsHandlers.trackNegativeFeedback) {
      analyticsHandlers.trackNegativeFeedback(feedbackSelection, feedbackDetails, responseId);
    }
  };
  const handleClose = () => {
    closeFeedback();
    focusComposerDelayed();
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    if (analyticsHandlers.trackCopy) {
      analyticsHandlers.trackCopy();
    }
  };
  if (status === MESSAGE_STATUS.ERROR) {
    return <div className={aiMessageContainerCss}>
        {/* Invisible marker for top visibility tracking */}
        <div ref={topElementRef} className={visibilityTrackerCss} data-testid="visibility-tracker" />
        <div className={errorContainerCss}>
          <ContextualBanner variant="danger" icon={AlertWarningTriangleIcon}>
            {content}
          </ContextualBanner>
        </div>
        {/* Invisible marker for bottom visibility tracking */}
        <div ref={bottomElementRef} className={visibilityTrackerCss} data-testid="visibility-tracker" />
      </div>;
  }
  return <div className={aiMessageContainerCss} ref={responseMessageRef} tabIndex={-1}>
      {/* Invisible marker for top visibility tracking */}
      <div ref={topElementRef} className={visibilityTrackerCss} data-testid="visibility-tracker" />
      <Markdown analyticsHandlers={analyticsHandlers}>{content}</Markdown>
      {/* Invisible marker for bottom visibility tracking */}
      <div ref={bottomElementRef} className={visibilityTrackerCss} data-testid="visibility-tracker" />
      {content && content.trim() !== '' && <div className={messageActionsCss}>
          <IconButtons onCopy={handleCopy} onLike={handleLike} onDislike={handleDislike} isLiked={isLiked} isDisliked={isDisliked} />
        </div>}
      {isFeedbackOpen && <FeedbackPopup onConfirm={handleConfirm} onClose={handleClose} />}
    </div>;
});

require("./AiResponseMessage.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AiResponseMessage.js");